import { useState, useCallback, useEffect, useRef } from 'react';
import {
  analyzeDependencies,
  autoCreateResources,
  initializeCloudHierarchy,
  type MissingResource,
  type ArchitectureSuggestion,
  type TerraformDependency
} from '../api/dependencies';

interface UseDependencyDetectionOptions {
  architectureId: string | null;
  provider: 'azure' | 'aws' | 'gcp' | 'fabric';
  enabled?: boolean;
  autoInitializeHierarchy?: boolean;
}

interface DependencyDetectionResult {
  // État
  missingResources: MissingResource[];
  suggestions: ArchitectureSuggestion[];
  dependencies: TerraformDependency[];
  suggestedEdges: any[];
  isAnalyzing: boolean;
  hasIssues: boolean;

  // Actions
  analyzeCurrentDiagram: (diagramData: { nodes: any[]; edges: any[] }) => Promise<void>;
  createMissingResources: (resources: MissingResource[], diagramData: { nodes: any[]; edges: any[] }) => Promise<{ nodes: any[]; edges: any[] } | null>;
  initializeHierarchy: (diagramData: { nodes: any[]; edges: any[] }) => Promise<{ nodes: any[]; edges: any[] } | null>;
  clearDetection: () => void;
  resetHierarchy: () => void;
}

/**
 * Hook pour détecter automatiquement les dépendances Terraform
 *
 * @param options - Options de configuration
 * @returns État et actions pour gérer les dépendances
 */
export function useDependencyDetection(
  options: UseDependencyDetectionOptions
): DependencyDetectionResult {
  const { architectureId, provider, enabled = true, autoInitializeHierarchy = true } = options;

  const [missingResources, setMissingResources] = useState<MissingResource[]>([]);
  const [suggestions, setSuggestions] = useState<ArchitectureSuggestion[]>([]);
  const [dependencies, setDependencies] = useState<TerraformDependency[]>([]);
  const [suggestedEdges, setSuggestedEdges] = useState<any[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const hierarchyInitializedRef = useRef(false);

  /**
   * Analyse le diagramme actuel pour détecter les dépendances
   */
  const analyzeCurrentDiagram = useCallback(async (diagramData: { nodes: any[]; edges: any[] }) => {
    if (!architectureId || !enabled) return;

    // Ne rien faire si le diagramme est vide
    if (!diagramData.nodes || diagramData.nodes.length === 0) {
      setMissingResources([]);
      setSuggestions([]);
      setDependencies([]);
      setSuggestedEdges([]);
      return;
    }

    setIsAnalyzing(true);

    try {
      // Analyser les dépendances via Terraform
      const analysis = await analyzeDependencies(architectureId, diagramData);

      setDependencies(analysis.dependencies);
      setMissingResources(analysis.missingResources);
      setSuggestions(analysis.suggestions);
      setSuggestedEdges(analysis.suggestedEdges || []);

      console.log('🔍 Dependency analysis completed:', {
        dependencies: analysis.dependencies.length,
        missing: analysis.missingResources.length,
        suggestions: analysis.suggestions.length
      });
    } catch (error: any) {
      console.error('Error analyzing dependencies:', error);
      // Ne pas afficher d'erreur à l'utilisateur pour éviter de polluer l'UI
      // L'analyse est optionnelle et peut échouer si le code Terraform n'est pas encore valide
    } finally {
      setIsAnalyzing(false);
    }
  }, [architectureId, enabled]);

  /**
   * Crée automatiquement les ressources manquantes
   */
  const createMissingResources = useCallback(async (
    resources: MissingResource[],
    diagramData: { nodes: any[]; edges: any[] }
  ): Promise<{ nodes: any[]; edges: any[] } | null> => {
    if (!architectureId) return null;

    try {
      const result = await autoCreateResources(architectureId, diagramData, resources);

      // Mettre à jour l'état local
      setMissingResources(prev => prev.filter(r => !resources.includes(r)));

      console.log(`✅ Created ${result.createdNodes.length} missing resources`);

      return result.updatedDiagramData;
    } catch (error: any) {
      console.error('Error creating missing resources:', error);
      throw error;
    }
  }, [architectureId]);

  /**
   * Initialise la hiérarchie cloud (Resource Groups, VPCs, etc.)
   */
  const initializeHierarchy = useCallback(async (
    diagramData: { nodes: any[]; edges: any[] }
  ): Promise<{ nodes: any[]; edges: any[] } | null> => {
    if (!architectureId || hierarchyInitializedRef.current) return null;

    try {
      const result = await initializeCloudHierarchy(architectureId, diagramData);

      hierarchyInitializedRef.current = true;

      console.log(`🏗️ Initialized cloud hierarchy: ${result.createdNodes.length} containers created`);

      return result.updatedDiagramData;
    } catch (error: any) {
      console.error('Error initializing hierarchy:', error);
      return null;
    }
  }, [architectureId]);

  /**
   * Réinitialise la détection
   */
  const clearDetection = useCallback(() => {
    setMissingResources([]);
    setSuggestions([]);
    setDependencies([]);
    setSuggestedEdges([]);
  }, []);

  /**
   * Réinitialise la hiérarchie pour permettre une nouvelle initialisation
   */
  const resetHierarchy = useCallback(() => {
    hierarchyInitializedRef.current = false;
  }, []);

  /**
   * Auto-initialiser la hiérarchie au premier node ajouté
   */
  useEffect(() => {
    // Reset quand on change d'architecture
    hierarchyInitializedRef.current = false;
  }, [architectureId]);

  return {
    // État
    missingResources,
    suggestions,
    dependencies,
    suggestedEdges,
    isAnalyzing,
    hasIssues: missingResources.length > 0 || suggestions.length > 0,

    // Actions
    analyzeCurrentDiagram,
    createMissingResources,
    initializeHierarchy,
    clearDetection,
    resetHierarchy
  };
}

export default useDependencyDetection;
